import { useState, useRef, ReactNode } from "react";
import Button from "@mui/material/Button";
import ButtonGroup, { ButtonGroupProps } from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper, { PopperProps } from "@mui/material/Popper";
import MenuList from "@mui/material/MenuList";
import { Icon } from "./Icon";
import { IconMenuItem } from "./ContextMenu";
import { CircularProgress } from "@mui/material";

export type SplitButtonOption = {
  label: string;
  icon?: string | ReactNode;
  onClick: (...args: any[]) => void;
  disabled?: boolean;

};

export type SplitButtonProps = {
  options: SplitButtonOption[];
  /** Enables ability to change the primary button based on index selection */
  allowSelectIndex?: boolean;
  /** Hides the selected index in the dropdown list. If allowSelectedIndex is false, hides the first option. */
  hideSelectedIndex?: boolean;
  /** If true, the menu will grow to the size of the button */
  matchButtonWidth?: boolean;
  PopperProps?: Omit<PopperProps, "open" | "anchorEl" | "role" | "transition" | "disablePortal">;
  loading?: boolean;
} & ButtonGroupProps;

export const SplitButton = ({ options, allowSelectIndex, hideSelectedIndex, matchButtonWidth, PopperProps: popperProps, loading, ...props }: SplitButtonProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    if (allowSelectIndex) {
      // set the index, ie change the main button
      setSelectedIndex(index);
    } else {
      // execute the action
      options[index].onClick();
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const mainButton:SplitButtonOption | undefined = options[selectedIndex];
  if (!mainButton) return null;
  const mainIcon = mainButton.icon ? (typeof mainButton.icon === "string" ? <Icon icon={mainButton.icon} /> : mainButton.icon) : undefined;
  const width = matchButtonWidth ? anchorRef.current?.clientWidth : undefined;

  return (
    <>
      <ButtonGroup variant="contained" ref={anchorRef} aria-label="split button" {...props}>
        <Button
          onClick={mainButton.onClick}
          startIcon={mainIcon}
          disabled={mainButton.disabled}
        >
          {mainButton.label}
        </Button>
        <Button
          sx={{ padding: "0.375rem 0.5rem" }}
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          {loading ? <CircularProgress size={16} /> : <Icon icon={open ? "chevronup" : "chevrondown"} />}
        </Button>
      </ButtonGroup>
      <Popper
        placement="bottom"
        {...popperProps}
        sx={{ ...popperProps?.sx, zIndex: 1, width }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper sx={{ mt: 0.5 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) =>
                    hideSelectedIndex && index === selectedIndex
                      ? null
                      : (
                        <IconMenuItem
                          key={option.label}
                          icon={option.icon}
                          selected={allowSelectIndex && index === selectedIndex}
                          onClick={(event: any) => handleMenuItemClick(event, index)}
                          disabled={option.disabled}
                        >
                          {option.label}
                        </IconMenuItem>
                      ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};